import React from 'react';
import { Helmet } from 'react-helmet';
import get from 'lodash/get';
import gql from 'graphql-tag';
import { GraphQLClient } from 'graphql-request';
import config from '../config';

import { GET_PRICE } from '../GraphQL/queries/PricePlan';
import {
  GET_VISIBLE_SETS,
  PUBLIC_SET,
  PUBLIC_SET_WITH_EWEBINAR_FRAGMENT,
} from '../GraphQL/queries/EWebinar';
import { PUBLIC_TEAM_FRAGMENT } from '../GraphQL/queries/TeamFragment';
import {
  PublicSetWithEWebinarFragment,
  PublicTeamFragment,
  PublicSetAndSessionFragment,
  SubscriptionStatus,
  PricePlanFragment,
} from '../GraphQL/schema/schema';
import { SessionCalculator } from '../shared/frontend/utils/SessionCalculator';
import { activateLanguage } from '../shared/frontend/utils/i18n';
import { LastTimezoneOnEarth } from './LocalTimeZone';

export interface SSRTeamData extends PublicTeamFragment {
  publicSets: PublicSetAndSessionFragment[];
}

export const asyncFilter = async <T,>(
  arr: T[],
  predicate: (i: T) => Promise<boolean>
): Promise<T[]> => {
  const results = await Promise.all(arr.map(predicate));
  return arr.filter((_v, index) => results[index]);
};

const hasSession = async (set: PublicSetWithEWebinarFragment): Promise<boolean> => {
  const ewebinarSessionCalculator = new SessionCalculator(set.publicWebinar!, LastTimezoneOnEarth);
  const sessions = await ewebinarSessionCalculator.getEwebinarSessions();
  const hasNoSession = sessions.sessions.length === 0;
  return !hasNoSession;
};

const isNotSuspended = (set: PublicSetWithEWebinarFragment): boolean => {
  const team = set.team as unknown as { subscriptionStatus: SubscriptionStatus };
  const isSuspended = [SubscriptionStatus.Suspended, SubscriptionStatus.Incomplete].includes(
    team.subscriptionStatus
  );
  return !isSuspended;
};

const client = new GraphQLClient(config.GRAPHQL_SERVER_URL);

export const getPublicSets = async (): Promise<PublicSetWithEWebinarFragment[]> => {
  // console.time('query publicSets()');

  let nextCursor: string | undefined;
  const publicSets: PublicSetWithEWebinarFragment[] = [];
  await activateLanguage('en');

  do {
    const publicSetQuery = await client.request(
      gql`
        query publicSets($teamId: String, $nextCursor: String) {
          publicSets(teamId: $teamId, nextCursor: $nextCursor) {
            sets {
              ...PublicSetWithEWebinar
              team {
                subscriptionStatus
                settings {
                  gdpr {
                    bannerMode
                    bannerText
                    bannerCode
                  }
                }
              }
            }
            nextCursor
          }
        }
        ${PUBLIC_SET_WITH_EWEBINAR_FRAGMENT}
      `,
      {
        nextCursor,
      }
    );

    const rows = get(publicSetQuery, 'publicSets.sets', []);

    publicSets.push(...rows);
    nextCursor = get(publicSetQuery, 'publicSets.nextCursor', '');
  } while (nextCursor);

  const notSuspendedSets = publicSets.filter(isNotSuspended);
  const result = await asyncFilter(notSuspendedSets, hasSession);

  // console.timeEnd('query publicSets()');

  return result;
};

export const getPublicSet = async (
  setId: string
): Promise<PublicSetWithEWebinarFragment | undefined> => {
  const publicSetQuery = await client.request(PUBLIC_SET, { id: setId });
  const set = get(publicSetQuery, 'publicSet', undefined);
  return set;
};

export const getTeams = async (): Promise<SSRTeamData[]> => {
  // console.time('query searchTeams()');
  let nextCursor: string | undefined;
  const teams: PublicTeamFragment[] = [];

  do {
    const teamsQuery = await client.request(
      gql`
        query searchTeams($filter: TeamsInput!) {
          searchTeams(filter: $filter) {
            data {
              ...PublicTeam
            }
            nextCursor
          }
        }
        ${PUBLIC_TEAM_FRAGMENT}
      `,
      {
        filter: {
          nextCursor,
        },
      }
    );

    const rows = get(teamsQuery, 'searchTeams.data', []);
    teams.push(...rows);
    nextCursor = get(teamsQuery, 'searchTeams.nextCursor', '');
  } while (nextCursor);
  // console.timeEnd('query searchTeams()');

  // console.time('query visibleSets()');

  const results: SSRTeamData[] = [];

  for (const team of teams) {
    const visibleSetsQuery = await client.request(GET_VISIBLE_SETS, {
      teamId: team.id,
      nextCursor: '',
    });

    const rows = get(visibleSetsQuery, 'visibleSets.sets', []);

    results.push({
      ...team,
      publicSets: rows,
    });
  }

  // console.timeEnd('query visibleSets()');

  return results;
};

export const getRegularPricePlan = async (): Promise<PricePlanFragment> => {
  const regularPricePlanQuery = await client.request(GET_PRICE);
  return get(regularPricePlanQuery, 'price', {});
};

// inserts a <meta />, which will be detected by Gatsby SSR to abort JS removal
export const ForceUseBundleComponent: React.FC = () => {
  const { name, content } = config.FORCE_BUNDLE_META;
  return (
    <Helmet>
      <meta name={name} content={content} />
    </Helmet>
  );
};
